// src/components/inventory/InventoryActions.js
// ✅ COMPLETE DROP-IN REPLACEMENT - ALL ORIGINAL CODE PRESERVED
// ✅ ADDED: Netuno template support
// ✅ ADDED: Better column mapping for description field
// ✅ ADDED: Progress indicators for large imports
// ✅ ADDED: Image preservation on re-upload
// ✅ ADDED: Batch processing for 928+ items
// ✅ KEPT: All original field packs, templates, and modals
// ✅ KEPT: Portal dropdown functionality
// ✅ KEPT: All UI styling and components

import React, { useRef, useState, useEffect, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  addDoc,
  doc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { Sparkles, ChevronDown, Upload, FileText, AlertTriangle, Loader2, Download } from "lucide-react";

const DEFAULT_GROUP_IDS = ["grp_all_staff"];

// --- Small Portal helper (no deps) ---
function Portal({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

const InventoryActions = ({ companyId, onInventoryUpdate, sampleOrders = [] }) => {
  const db = getFirestore();
  const auth = getAuth();

  const fileInputRef = useRef(null);

  // Actions dropdown refs
  const actionsBtnRef = useRef(null);
  const actionsMenuRef = useRef(null);

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // ✅ ADDED: Progress tracking for large imports
  const [importProgress, setImportProgress] = useState(0);
  const [importStatus, setImportStatus] = useState("");

  // inventory field definitions (for custom fields)
  const [fieldDefinitions, setFieldDefinitions] = useState([]);

  // template / pack picker
  const [templateId, setTemplateId] = useState("seafood");

  // field pack modal + apply state
  const [showPackModal, setShowPackModal] = useState(false);
  const [applyingPack, setApplyingPack] = useState(false);
  const [packError, setPackError] = useState("");
  const [selectedExtraFieldKeys, setSelectedExtraFieldKeys] = useState([]);

  // import confirm modal state
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImportRows, setPendingImportRows] = useState([]);
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewError, setImportPreviewError] = useState("");
  
  // ✅ ADDED: Column mapping for description and other fields
  const [, setColumnMapping] = useState({});
  const [showColumnMapper, setShowColumnMapper] = useState(false);

  // actions dropdown
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });

  const uniqStrings = (arr) =>
    Array.from(
      new Set(
        (Array.isArray(arr) ? arr : [])
          .filter(Boolean)
          .map((v) => String(v).trim())
      )
    ).filter(Boolean);

  const normalizeKey = (value) => {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "_");
  };

  const INVENTORY_SHEET_HEADERS = {
    sku: "SKU",
    name: "Product",
    description: "Description",
    category: "Category",
    subCategory: "Subcategory",
    packSize: "Pack",
    price: "Price",
    stock: "Stock",
    lowStockThreshold: "Low Stock",
    sampleable: "Sample-able",
    featured: "Featured",
    groupIds: "Group IDs",
    sampleOrderCount: "Sample Orders",
    convertedOrderCount: "Converted Orders",
    conversionToOrderPercent: "Conversion to Order %",
  };

  const INVENTORY_SHEET_ORDER = [
    "sku",
    "description",
    "name",
    "category",
    "subCategory",
    "packSize",
    "price",
    "stock",
    "lowStockThreshold",
    "sampleable",
    "featured",
    "groupIds",
    "sampleOrderCount",
    "convertedOrderCount",
    "conversionToOrderPercent",
  ];

  const formatInventorySheetRow = (row) => {
    const formatted = {};
    const source = row || {};

    INVENTORY_SHEET_ORDER.forEach((key) => {
      if (!(key in source)) return;
      formatted[INVENTORY_SHEET_HEADERS[key] || key] = source[key];
    });

    Object.entries(source).forEach(([key, value]) => {
      if (key in formatted || INVENTORY_SHEET_ORDER.includes(key)) return;
      formatted[INVENTORY_SHEET_HEADERS[key] || key] = value;
    });
    return formatted;
  };

  const normalizeSku = (value) => {
    const raw = String(value ?? "").trim();
    if (!raw) return "";
    return raw.toLowerCase();
  };

  const registerSkuAlias = (map, duplicates, skuValue, record) => {
    const skuNorm = normalizeSku(skuValue);
    if (!skuNorm) return;
    const existing = map[skuNorm];
    if (existing && String(existing.id || "") !== String(record.id || "")) {
      duplicates.add(skuNorm);
      delete map[skuNorm];
      return;
    }
    if (!duplicates.has(skuNorm)) {
      map[skuNorm] = record;
    }
  };

  const sampleOrderMetricsByProduct = useMemo(() => {
    const metrics = new Map();
    const orders = Array.isArray(sampleOrders) ? sampleOrders : [];

    const isConverted = (order) => {
      if (order?.convertedToOrder === true) return true;
      const status = String(order?.status || "").trim().toLowerCase();
      return status === "converted to order";
    };

    orders.forEach((order) => {
      const rawItems =
        Array.isArray(order?.items) && order.items.length > 0
          ? order.items
          : order?.productId
          ? [{ productId: order.productId }]
          : [];

      const converted = isConverted(order);
      const seenProducts = new Set();

      rawItems.forEach((item) => {
        const productId = String(item?.productId || "").trim();
        if (!productId || seenProducts.has(productId)) return;
        seenProducts.add(productId);

        const current = metrics.get(productId) || {
          sampleOrderCount: 0,
          convertedOrderCount: 0,
        };

        current.sampleOrderCount += 1;
        if (converted) current.convertedOrderCount += 1;

        metrics.set(productId, current);
      });
    });

    return metrics;
  }, [sampleOrders]);

  const parseBoolean = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "boolean") return value;

    const v = String(value).trim().toLowerCase();
    // ✅ ADDED: Support for "yes-" and "true-" variants
    return v === "true" || v === "yes" || v === "y" || v === "1" || v === "yes-" || v === "true-";
  };

  const safeNumber = (value, fallback = 0) => {
    if (value === "" || value === null || value === undefined) return fallback;
    if (typeof value === "number" && Number.isFinite(value)) return value;

    const cleaned = String(value).replace(/[$,]/g, "").trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : fallback;
  };

  /**
   * Parse groupIds from a spreadsheet cell.
   * - If blank/undefined => specified=false
   * - If "clear" / "none" / "empty" => specified=true, ids=[]
   * - If "grp_a, grp_b" => specified=true, ids=[...]
   */
  const parseGroupIdsCell = (value) => {
    if (value === null || value === undefined) return { specified: false, ids: [] };

    if (Array.isArray(value)) return { specified: true, ids: uniqStrings(value) };

    const raw = String(value).trim();
    if (!raw) return { specified: false, ids: [] };

    const lowered = raw.toLowerCase();
    if (["clear", "none", "empty", "null"].includes(lowered)) {
      return { specified: true, ids: [] };
    }

    const parts = raw
      .split(/[,;|]/g)
      .map((s) => s.trim())
      .filter(Boolean);

    return { specified: true, ids: uniqStrings(parts) };
  };

  /**
   * Category/SubCategory behavior:
   * - Preferred: category="Seafood" and subCategory="Salmon"
   * - Backward compatible: category="Seafood - Salmon" and missing subCategory => split automatically
   */
  const splitCategoryIfNeeded = (categoryRaw, subCategoryRaw) => {
    const cat = String(categoryRaw ?? "").trim();
    const sub = String(subCategoryRaw ?? "").trim();

    if (sub) return { category: cat || null, subCategory: sub || null };

    if (cat.includes(" - ")) {
      const [a, b] = cat.split(" - ", 2);
      return {
        category: String(a || "").trim() || null,
        subCategory: String(b || "").trim() || null,
      };
    }

    return { category: cat || null, subCategory: null };
  };

  const loadDefs = async (compId) => {
    if (!compId) return;
    try {
      const colRef = collection(db, "companies", compId, "inventoryFieldDefinitions");
      const snap = await getDocs(colRef);
      const defs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      defs.sort((a, b) => {
        const orderA = typeof a.order === "number" ? a.order : 9999;
        const orderB = typeof b.order === "number" ? b.order : 9999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.label || "")
          .toLowerCase()
          .localeCompare((b.label || "").toLowerCase());
      });

      setFieldDefinitions(defs);
    } catch (err) {
      console.error("[InventoryActions] Error loading inventoryFieldDefinitions:", err);
    }
  };

  useEffect(() => {
    loadDefs(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ✅ ADDED: Netuno template to TEMPLATES
  const TEMPLATES = useMemo(
    () => [
      { id: "seafood", label: "Seafood" },
      { id: "meat", label: "Meat" },
      { id: "produce", label: "Produce" },
      { id: "beverage", label: "Beverage" },
      { id: "alcohol", label: "Alcohol" },
      { id: "bakery", label: "Bakery" },
      { id: "dry_goods", label: "Dry Goods" },
      { id: "custom_select", label: "Custom Select" },
      { id: "netuno", label: "Netuno Format" }, // ✅ ADDED
    ],
    []
  );

  // --- Field Packs (4–8 columns each) ---
  const FIELD_PACKS = useMemo(
    () => ({
      seafood: {
        title: "Seafood Field Pack",
        description:
          "Common distributor fields for seafood items: origin, cut, handling, and product attributes.",
        fields: [
          { key: "species", label: "Species", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "cut_type", label: "Cut Type", type: "select", options: ["Fillet", "Portion", "Loin", "Steak", "Whole", "Other"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "fresh_frozen", label: "Fresh/Frozen", type: "select", options: ["Fresh", "Frozen", "Previously Frozen"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "origin", label: "Origin", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "wild_farmed", label: "Wild/Farmed", type: "select", options: ["Wild", "Farmed", "Unknown"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "glaze_pct", label: "Glaze % (if frozen)", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "iqf", label: "IQF", type: "select", options: ["Yes", "No"], showInTable: false, showInEdit: true, showInCatalog: false },
        ],
      },

      meat: {
        title: "Meat Field Pack",
        description: "Common meat fields: cut details, grade, weights, and handling.",
        fields: [
          { key: "protein_type", label: "Protein Type", type: "select", options: ["Beef", "Pork", "Poultry", "Lamb", "Other"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "cut", label: "Cut", type: "text", showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "grade", label: "Grade", type: "select", options: ["Prime", "Choice", "Select", "Standard", "Other"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "case_weight_lb", label: "Case Weight (lb)", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "avg_piece_weight_oz", label: "Avg Piece Weight (oz)", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "fresh_frozen", label: "Fresh/Frozen", type: "select", options: ["Fresh", "Frozen", "Previously Frozen"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "origin", label: "Origin", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
        ],
      },

      produce: {
        title: "Produce Field Pack",
        description:
          "Produce fields distributors commonly track: organic, origin, sizing, ripeness, and pack details.",
        fields: [
          { key: "organic", label: "Organic", type: "select", options: ["Yes", "No"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "country_of_origin", label: "Country of Origin", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "variety", label: "Variety", type: "text", showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "size_grade", label: "Size/Grade", type: "select", options: ["Small", "Medium", "Large", "XL", "Jumbo", "Mixed"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "case_count", label: "Case Count", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "ripeness", label: "Ripeness", type: "select", options: ["Green", "Ready", "Ripe", "Mixed", "N/A"], showInTable: false, showInEdit: true, showInCatalog: false },
        ],
      },

      beverage: {
        title: "Beverage Field Pack",
        description: "Beverage fields: style, packaging, ABV/IBU where applicable, and deposit.",
        fields: [
          { key: "style", label: "Style", type: "text", showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "package_type", label: "Package Type", type: "select", options: ["Can", "Bottle", "Keg", "Bag-in-Box", "Other"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "unit_volume", label: "Unit Volume", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "abv", label: "ABV", type: "number", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "ibu", label: "IBU", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "deposit", label: "Deposit", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
        ],
      },

      alcohol: {
        title: "Alcohol Field Pack",
        description: "ABV-forward fields for beer, wine, and spirits with sales-ready details.",
        fields: [
          { key: "alcohol_type", label: "Alcohol Type", type: "select", options: ["Beer", "Wine", "Spirits", "Cider", "Sake", "Other"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "region", label: "Region", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "vintage", label: "Vintage", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "abv", label: "ABV", type: "number", showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "proof", label: "Proof", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "case_equivalent", label: "Case Equivalent", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "serving_format", label: "Serving Format", type: "select", options: ["Bottle", "Can", "Draft", "Box", "Other"], showInTable: true, showInEdit: true, showInCatalog: true },
        ],
      },

      bakery: {
        title: "Bakery Field Pack",
        description: "Shelf-life, thaw, and finish details for breads, pastries, and desserts.",
        fields: [
          { key: "baked_type", label: "Baked Type", type: "select", options: ["Bread", "Pastry", "Dessert", "Cake", "Cookie", "Other"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "par_baked", label: "Par Baked", type: "select", options: ["Yes", "No"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "thaw_time", label: "Thaw Time", type: "text", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "bake_time", label: "Bake Time", type: "text", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "shelf_life_days", label: "Shelf Life (days)", type: "number", showInTable: true, showInEdit: true, showInCatalog: false },
          { key: "allergens", label: "Allergens", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "display_case_ready", label: "Display Case Ready", type: "select", options: ["Yes", "No"], showInTable: false, showInEdit: true, showInCatalog: true },
        ],
      },

      dry_goods: {
        title: "Dry Goods Field Pack",
        description: "Dry goods fields: brand, UOM, allergens, storage, and dietary flags.",
        fields: [
          { key: "brand", label: "Brand", type: "text", showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "uom", label: "Unit of Measure", type: "select", options: ["Each", "Case", "Bag", "Box", "Jar", "Bottle", "Other"], showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "allergens", label: "Allergens", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "storage", label: "Storage", type: "select", options: ["Ambient", "Refrigerated", "Frozen"], showInTable: true, showInEdit: true, showInCatalog: true },
          { key: "case_pack", label: "Case Pack", type: "text", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "gluten_free", label: "Gluten Free", type: "select", options: ["Yes", "No", "Unknown"], showInTable: false, showInEdit: true, showInCatalog: true },
        ],
      },

      custom_select: {
        title: "Custom Select",
        description: "Start blank, then choose any combination of fields from the full template library.",
        fields: [],
      },
      
      // ✅ ADDED: Netuno field pack (can be empty or with specific fields)
      netuno: {
        title: "Netuno Field Pack",
        description: "Common fields for Netuno inventory imports.",
        fields: [
          { key: "netuno_id", label: "Netuno ID", type: "text", showInTable: true, showInEdit: true, showInCatalog: false },
          { key: "supplier", label: "Supplier", type: "text", showInTable: false, showInEdit: true, showInCatalog: true },
          { key: "unit_weight", label: "Unit Weight", type: "number", showInTable: false, showInEdit: true, showInCatalog: false },
          { key: "seasonal", label: "Seasonal", type: "select", options: ["Yes", "No"], showInTable: false, showInEdit: true, showInCatalog: true },
        ],
      },
    }),
    []
  );

  const getTemplateRows = (id) => {
    switch (id) {
      case "seafood":
        return [
          {
            sku: "10010",
            name: "Atlantic Salmon Fillet 8 oz",
            category: "Seafood",
            subCategory: "Salmon",
            packSize: "10 x 8 oz",
            price: "7.25",
            stock: "120",
            lowStockThreshold: "20",
            sampleable: "Yes",
            featured: "Yes",
            description: "Fresh Atlantic salmon fillet, skin-on, 8oz portions", // ✅ ADDED description
          },
          {
            sku: "10020",
            name: "Gulf Shrimp 16/20 (2 lb bag)",
            category: "Seafood",
            subCategory: "Shrimp",
            packSize: "6 x 2 lb",
            price: "12.99",
            stock: "48",
            lowStockThreshold: "10",
            sampleable: "Yes",
            featured: "No",
            description: "Wild-caught Gulf shrimp, 16-20 count per pound", // ✅ ADDED description
          },
        ];
      case "meat":
        return [
          {
            sku: "20010",
            name: "Ribeye Steak 12 oz",
            category: "Meat",
            subCategory: "Beef",
            packSize: "12 x 12 oz",
            price: "9.95",
            stock: "36",
            lowStockThreshold: "12",
            sampleable: "No",
            featured: "Yes",
            description: "USDA Choice ribeye steak, 12oz portions", // ✅ ADDED description
          },
          {
            sku: "20020",
            name: "Chicken Breast Boneless/Skinless",
            category: "Meat",
            subCategory: "Poultry",
            packSize: "40 lb case",
            price: "2.85",
            stock: "10",
            lowStockThreshold: "4",
            sampleable: "Yes",
            featured: "No",
            description: "Boneless skinless chicken breast, vacuum packed", // ✅ ADDED description
          },
        ];
      case "produce":
        return [
          {
            sku: "30010",
            name: "Roma Tomatoes",
            category: "Produce",
            subCategory: "Tomatoes",
            packSize: "25 lb",
            price: "1.35",
            stock: "14",
            lowStockThreshold: "6",
            sampleable: "No",
            featured: "No",
            description: "Fresh Roma tomatoes, case of 25lbs", // ✅ ADDED description
          },
          {
            sku: "30020",
            name: "Avocados Hass (48ct)",
            category: "Produce",
            subCategory: "Avocado",
            packSize: "48 ct",
            price: "0.95",
            stock: "8",
            lowStockThreshold: "4",
            sampleable: "No",
            featured: "Yes",
            description: "Hass avocados, 48 count case", // ✅ ADDED description
          },
        ];
      case "beverage":
        return [
          {
            sku: "40010",
            name: "Sparkling Water - Lime",
            category: "Beverage",
            subCategory: "Water",
            packSize: "24 x 12 oz",
            price: "0.55",
            stock: "120",
            lowStockThreshold: "24",
            sampleable: "Yes",
            featured: "No",
            description: "Sparkling water with natural lime flavor", // ✅ ADDED description
          },
          {
            sku: "40020",
            name: "Cold Brew Coffee Concentrate",
            category: "Beverage",
            subCategory: "Coffee",
            packSize: "6 x 32 oz",
            price: "6.75",
            stock: "22",
            lowStockThreshold: "8",
            sampleable: "Yes",
            featured: "Yes",
            description: "Cold brew coffee concentrate, ready to serve", // ✅ ADDED description
          },
        ];
      case "dry_goods":
        return [
          {
            sku: "50010",
            name: "Olive Oil Extra Virgin",
            category: "Dry Goods",
            subCategory: "Oils",
            packSize: "6 x 1 L",
            price: "8.50",
            stock: "18",
            lowStockThreshold: "6",
            sampleable: "No",
            featured: "Yes",
            description: "Extra virgin olive oil, first cold press", // ✅ ADDED description
          },
          {
            sku: "50020",
            name: "All-Purpose Flour",
            category: "Dry Goods",
            subCategory: "Baking",
            packSize: "50 lb",
            price: "19.99",
            stock: "6",
            lowStockThreshold: "2",
            sampleable: "No",
            featured: "No",
            description: "All-purpose flour, 50lb bag", // ✅ ADDED description
          },
        ];
      case "netuno": // ✅ ADDED: Netuno template
        return [
          {
            "Item Code": "NTN-001",
            "Product Name": "Atlantic Salmon Fillet",
            "Category": "Seafood",
            "Sub-Category": "Salmon",
            "Pack Size": "10x8oz",
            "Price": "7.25",
            "On Hand": "120",
            "Reorder Point": "20",
            "Sample-able": "Yes-",
            "Featured": "Yes",
            "Description": "Fresh Atlantic salmon fillet portions",
            "Supplier": "Ocean Fresh Seafood",
            "Unit Weight": "8",
          },
          {
            "Item Code": "NTN-002",
            "Product Name": "Gulf Shrimp 16/20",
            "Category": "Seafood",
            "Sub-Category": "Shrimp",
            "Pack Size": "6x2lb",
            "Price": "12.99",
            "On Hand": "48",
            "Reorder Point": "10",
            "Sample-able": "Yes-",
            "Featured": "No",
            "Description": "Wild-caught Gulf shrimp",
            "Supplier": "Gulf Coast Fisheries",
            "Unit Weight": "2",
          },
        ];
      case "alcohol":
        return [
          {
            sku: "61010",
            name: "House Cabernet Sauvignon",
            category: "Alcohol",
            subCategory: "Wine",
            packSize: "12 x 750ml",
            price: "98.00",
            stock: "14",
            lowStockThreshold: "4",
            sampleable: "No",
            featured: "Yes",
            description: "Medium-bodied cabernet with dark fruit and soft tannins",
          },
          {
            sku: "61020",
            name: "Local IPA Draft",
            category: "Alcohol",
            subCategory: "Beer",
            packSize: "1/2 bbl",
            price: "189.00",
            stock: "5",
            lowStockThreshold: "2",
            sampleable: "No",
            featured: "No",
            description: "Citrus-forward IPA available in half-barrel draft",
          },
        ];
      case "bakery":
        return [
          {
            sku: "71010",
            name: "Butter Croissant",
            category: "Bakery",
            subCategory: "Pastry",
            packSize: "48 ct",
            price: "42.00",
            stock: "22",
            lowStockThreshold: "6",
            sampleable: "Yes",
            featured: "Yes",
            description: "All-butter croissant, fully baked and frozen",
          },
          {
            sku: "71020",
            name: "Sourdough Sandwich Loaf",
            category: "Bakery",
            subCategory: "Bread",
            packSize: "12 ct",
            price: "28.50",
            stock: "10",
            lowStockThreshold: "3",
            sampleable: "Yes",
            featured: "No",
            description: "Rustic sourdough loaf, sliced for sandwich service",
          },
        ];
      case "custom_select":
      default:
        return [
          {
            sku: "10000",
            name: "Example Item Name",
            category: "Seafood",
            subCategory: "Salmon",
            packSize: "Case / Pack",
            price: "0.00",
            stock: "0",
            lowStockThreshold: "10",
            sampleable: "Yes",
            featured: "No",
            description: "Example product description", // ✅ ADDED description
          },
        ];
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  // ✅ IMPROVED: Better column detection with description support
  const detectColumnMapping = (columns) => {
    const mapping = {};
    const columnMap = {
      sku: ["sku", "code", "item code", "product code", "id", "item_id", "item code"],
      name: ["name", "product", "product name", "item", "item name", "product description"],
      description: ["description", "desc", "product description", "notes", "detail", "remarks"],
      category: ["category", "type", "product type", "group", "department"],
      subCategory: ["subcategory", "sub_category", "sub category", "subtype", "variety", "sub-category"],
      packSize: ["packsize", "pack size", "pack", "packaging", "unit", "uom", "pack size"],
      price: ["price", "cost", "unit price", "retail", "wholesale", "unit cost"],
      stock: ["stock", "inventory", "quantity", "qty", "on hand", "available", "on hand", "onhand"],
      lowStockThreshold: ["low stock", "reorder point", "minimum stock", "min qty", "threshold", "reorder level"],
      sampleable: ["sampleable", "sample-able", "samplable", "can sample", "sample", "sample eligible"],
      featured: ["featured", "feature", "promoted", "highlight", "special", "promo"],
      groupIds: ["groups", "group ids", "group_ids", "visibility", "access groups"],
    };

    columns.forEach((col) => {
      const colLower = col.toLowerCase().trim();
      
      // Try exact match first
      for (const [key, aliases] of Object.entries(columnMap)) {
        if (aliases.some(alias => colLower === alias.toLowerCase())) {
          mapping[col] = key;
          return;
        }
      }
      
      // Try partial match
      for (const [key, aliases] of Object.entries(columnMap)) {
        if (aliases.some(alias => colLower.includes(alias.toLowerCase()) || alias.toLowerCase().includes(colLower))) {
          mapping[col] = key;
          return;
        }
      }
      
      // Check if it's a custom field
      const fieldDef = fieldDefinitions.find(def => 
        def.key?.toLowerCase() === colLower || 
        def.label?.toLowerCase() === colLower
      );
      if (fieldDef) {
        mapping[col] = `custom:${fieldDef.key}`;
      }
    });

    return mapping;
  };

  const buildImportPreview = async (rows, columns = []) => {
    const invColRef = collection(db, "companies", companyId, "inventory");
    const existingInventory = await getDocs(invColRef);

    const existingBySku = {};
    const duplicateSkuSet = new Set();

    existingInventory.docs.forEach((d) => {
      const item = d.data();
      const record = { id: d.id, ...item };
      registerSkuAlias(existingBySku, duplicateSkuSet, item?.sku, record);
      (Array.isArray(item?.previousSkus) ? item.previousSkus : []).forEach((previousSku) => {
        registerSkuAlias(existingBySku, duplicateSkuSet, previousSku, record);
      });
    });

    const COL_SCAN_MAX = Math.min(rows.length, 25);
    const colSetRaw = new Set();
    for (let i = 0; i < COL_SCAN_MAX; i++) {
      Object.keys(rows[i] || {}).forEach((k) => colSetRaw.add(String(k)));
    }
    const rawColumns = Array.from(colSetRaw);
    const normalizedColumns = rawColumns.map((c) => normalizeKey(c));
    const normalizedColSet = new Set(normalizedColumns);

    // ✅ IMPROVED: Auto-detect column mapping
    const detectedMapping = detectColumnMapping(rawColumns);
    setColumnMapping(detectedMapping);

    const hasAny = (aliases) => aliases.some((a) => normalizedColSet.has(normalizeKey(a)));

    const overwriteFields = [];
    if (hasAny(["sku"])) overwriteFields.push("sku");
    if (hasAny(["name"])) overwriteFields.push("name");
    if (hasAny(["description"])) overwriteFields.push("description"); // ✅ ADDED description
    if (hasAny(["category"])) overwriteFields.push("category");
    if (hasAny(["subCategory", "subcategory", "sub_category", "sub category"])) overwriteFields.push("subCategory");
    if (hasAny(["packSize", "pack_size", "pack size"])) overwriteFields.push("packSize");
    if (hasAny(["price"])) overwriteFields.push("price");
    if (
      hasAny([
        "stock",
        "onHandQty",
        "on_hand_qty",
        "onHand",
        "on_hand",
        "qtyOnHand",
        "qty_on_hand",
        "in stock",
        "in_stock",
        "on hand",
      ])
    )
      overwriteFields.push("stock");
    if (
      hasAny([
        "lowStockThreshold",
        "low_stock_threshold",
        "reorderPoint",
        "reorder_point",
        "minQty",
        "min_qty",
        "min stock",
        "min_stock",
        "reorder point",
      ])
    )
      overwriteFields.push("lowStockThreshold");
    if (hasAny(["sampleable", "sample-able"])) overwriteFields.push("sampleable");
    if (hasAny(["featured", "pushFlag", "push_flag"])) overwriteFields.push("featured");
    if (hasAny(["groupIds", "group_ids", "groups", "group ids"]))
      overwriteFields.push("groupIds (only if provided)");

    const customOverwrites = [];
    (fieldDefinitions || []).forEach((def) => {
      const defKey = normalizeKey(def.key || def.id || "");
      const defLabel = normalizeKey(def.label || "");
      if (!defKey && !defLabel) return;

      if (defKey && normalizedColSet.has(defKey)) customOverwrites.push(def.key || def.id);
      else if (defLabel && normalizedColSet.has(defLabel)) customOverwrites.push(def.label);
    });

    let willUpdate = 0;
    let willCreate = 0;
    let skipMissingSku = 0;
    let skipDuplicateSkuInDb = 0;
    let skipDuplicateSkuInFile = 0;

    const seenSkuInFile = new Set();

    rows.forEach((row) => {
      // Use column mapping to find SKU
      const skuKey = Object.keys(detectedMapping).find(k => detectedMapping[k] === 'sku') || 'sku';
      const skuRaw = row?.[skuKey] ?? row?.sku ?? row?.SKU ?? row?.Sku ?? "";
      const skuNorm = normalizeSku(skuRaw);

      if (!skuNorm) {
        skipMissingSku += 1;
        return;
      }

      if (seenSkuInFile.has(skuNorm)) {
        skipDuplicateSkuInFile += 1;
        return;
      }
      seenSkuInFile.add(skuNorm);

      if (duplicateSkuSet.has(skuNorm)) {
        skipDuplicateSkuInDb += 1;
        return;
      }

      if (existingBySku[skuNorm]) willUpdate += 1;
      else willCreate += 1;
    });

    return {
      rowsTotal: rows.length,
      columnsDetected: rawColumns.length,
      columnMapping: detectedMapping, // ✅ ADDED column mapping to preview
      overwriteFieldCount: overwriteFields.length + customOverwrites.length,
      overwriteFields,
      overwriteCustomFields: customOverwrites,
      willUpdate,
      willCreate,
      skipMissingSku,
      skipDuplicateSkuInDb,
      skipDuplicateSkuInFile,
      duplicateSkusInDbCount: duplicateSkuSet.size,
    };
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !companyId) return;

    setImportPreviewError("");
    setImporting(true);
    setImportStatus("Reading Excel file...");

    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, {
          defval: "",
          raw: false,
        });

        console.log("[InventoryImport] Parsed rows:", rows.length);

        const preview = await buildImportPreview(rows);

        setPendingImportRows(rows);
        setImportPreview(preview);
        setShowImportConfirm(true);
        
        // ✅ ADDED: Check if description column was detected
        const hasDescription = Object.values(preview.columnMapping).includes('description');
        if (!hasDescription && rows.some(row => 
          row.description || row.Description || row.desc || row.DESC
        )) {
          setShowColumnMapper(true);
        }
      } catch (err) {
        console.error("Import parse/preflight error:", err);
        setImportPreviewError("Failed to read this file. Please confirm it's a valid .xlsx template.");
      } finally {
        setImporting(false);
        setImportStatus("");
      }
    };

    reader.onerror = () => {
      setImportPreviewError("Failed to read file. Please try again.");
      setImporting(false);
      setImportStatus("");
    };

    reader.readAsArrayBuffer(file);
  };

  // ✅ IMPROVED: Batch import with progress tracking
  const importInventoryData = async (rows, mapping = null) => {
    if (!companyId || rows.length === 0) return;

    setImporting(true);
    setImportStatus(`Processing 0/${rows.length} items...`);
    setImportProgress(0);

    try {
      const invColRef = collection(db, "companies", companyId, "inventory");
      const existingInventory = await getDocs(invColRef);

      const existingBySku = {};
      const duplicateSkuSet = new Set();

      existingInventory.docs.forEach((d) => {
        const item = d.data();
        const record = { id: d.id, ...item };
        registerSkuAlias(existingBySku, duplicateSkuSet, item?.sku, record);
        (Array.isArray(item?.previousSkus) ? item.previousSkus : []).forEach((previousSku) => {
          registerSkuAlias(existingBySku, duplicateSkuSet, previousSku, record);
        });
      });

      const uid = auth?.currentUser?.uid || null;
      const processedSkuInFile = new Set();
      let batch = writeBatch(db);
      let batchCount = 0;
      const BATCH_SIZE = 100; // ✅ Process in batches for large imports

      // Use provided mapping or auto-detect
      const columnMap = mapping || importPreview?.columnMapping || {};

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        
        // Update progress
        setImportProgress(Math.round((i / rows.length) * 100));
        setImportStatus(`Processing ${i + 1}/${rows.length} items...`);

        // Find SKU using mapping
        const skuKey = Object.keys(columnMap).find(k => columnMap[k] === 'sku') || 'sku';
        const skuRaw = row[skuKey] ?? row.sku ?? row.SKU ?? row.Sku ?? "";
        const sku = String(skuRaw || "").trim();
        const skuNorm = normalizeSku(sku);

        if (!skuNorm) {
          console.warn("[InventoryImport] Skipping row (missing SKU):", row);
          continue;
        }

        if (processedSkuInFile.has(skuNorm)) {
          console.warn("[InventoryImport] Skipping row (duplicate SKU in file):", sku);
          continue;
        }
        processedSkuInFile.add(skuNorm);

        if (duplicateSkuSet.has(skuNorm)) {
          console.warn(
            "[InventoryImport] Skipping SKU because duplicates exist in Firestore:",
            sku
          );
          continue;
        }

        // Helper to get value using mapping
        const getMappedValue = (fieldName) => {
          const sourceKey = Object.keys(columnMap).find(k => columnMap[k] === fieldName);
          if (sourceKey && row[sourceKey] !== undefined) {
            return row[sourceKey];
          }
          // Fallback to direct field access
          return row[fieldName] !== undefined ? row[fieldName] : 
                 row[fieldName.toLowerCase()] !== undefined ? row[fieldName.toLowerCase()] :
                 row[fieldName.toUpperCase()] !== undefined ? row[fieldName.toUpperCase()] : '';
        };

        const name = String(getMappedValue('name') || '').trim();
        const description = String(
          getMappedValue('description') || 
          row.description || 
          row.Description || 
          row.desc || 
          row.DESC || 
          ''
        ).trim();

        const stockRaw = getMappedValue('stock') || row.onHand || row['on hand'] || row.quantity || row.qty;
        const lowStockRaw = getMappedValue('lowStockThreshold') || row.reorderPoint || row['reorder point'] || row.minStock;
        const featuredRaw = getMappedValue('featured') || row.pushFlag || row.feature;

        const categoryRaw = String(getMappedValue('category') || '').trim();
        const subCategoryRaw = String(getMappedValue('subCategory') || '').trim();

        const { category, subCategory } = splitCategoryIfNeeded(categoryRaw, subCategoryRaw);

        const groupCell = getMappedValue('groupIds') || row.groups;
        const parsedGroup = parseGroupIdsCell(groupCell);

        const existing = existingBySku[skuNorm] || null;
        const existingGroupIds = uniqStrings(existing?.groupIds);

        let finalGroupIds;
        if (!existing) {
          finalGroupIds = DEFAULT_GROUP_IDS;
        } else {
          if (parsedGroup.specified) finalGroupIds = parsedGroup.ids;
          else if (existingGroupIds.length > 0) finalGroupIds = existingGroupIds;
          else finalGroupIds = DEFAULT_GROUP_IDS;
        }

        const item = {
          sku: sku || null,
          name: name || null,
          description: description || null, // ✅ ADDED: Description field
          category: category || null,
          subCategory: subCategory || null,

          packSize:
            String(getMappedValue('packSize') || row.packSize || row['pack size'] || '').trim() || null,
          price:
            getMappedValue('price') !== "" && getMappedValue('price') !== null && getMappedValue('price') !== undefined
              ? safeNumber(getMappedValue('price'), null)
              : null,
          stock: safeNumber(stockRaw, 0),
          lowStockThreshold: safeNumber(lowStockRaw, 10),
          sampleable: parseBoolean(getMappedValue('sampleable') || row.sampleable),
          featured: parseBoolean(featuredRaw),
          updatedAt: serverTimestamp(),
          source: "excelImport",
          groupIds: finalGroupIds,
        };

        const currentExistingSku = String(existing?.sku || "").trim();
        const currentExistingSkuNorm = normalizeSku(currentExistingSku);
        if (existing && currentExistingSkuNorm && currentExistingSkuNorm !== skuNorm) {
          item.previousSkus = uniqStrings([
            ...(Array.isArray(existing?.previousSkus) ? existing.previousSkus : []),
            currentExistingSku,
          ]);
        }

        // ✅ PRESERVE existing image if updating
        if (existing && existing.imageUrl) {
          item.imageUrl = existing.imageUrl;
        }

        if (fieldDefinitions.length > 0) {
          const custom = existing?.custom || {};

          fieldDefinitions.forEach((def) => {
            const defKey = def.key || def.id;
            if (!defKey) return;

            const fieldType = String(def.fieldType || def.type || "text").toLowerCase();
            const mappedKey = Object.keys(columnMap).find(k => columnMap[k] === `custom:${defKey}`);
            
            let rawValue = mappedKey ? row[mappedKey] : undefined;
            if (rawValue === undefined || rawValue === null || rawValue === "") {
              // Keep existing value if not in import
              if (existing && custom[defKey] !== undefined) {
                return;
              }
              custom[defKey] = null;
              return;
            }

            if (fieldType === "number") {
              const n = safeNumber(rawValue, null);
              custom[defKey] = n;
            } else if (fieldType === "boolean") {
              custom[defKey] = ["true", "yes", "y", "1", "yes-", "true-"].includes(
                rawValue.toString().trim().toLowerCase()
              );
            } else {
              custom[defKey] = rawValue.toString().trim();
            }
          });

          if (Object.keys(custom).length > 0) item.custom = custom;
        }

        if (existing) {
          const updatePayload = {
            ...item,
            // Don't delete visibility fields if they exist
          };

          if (uid) updatePayload.updatedBy = uid;

          const itemRef = doc(db, "companies", companyId, "inventory", existing.id);
          batch.update(itemRef, updatePayload);
        } else {
          const newDocRef = doc(collection(db, "companies", companyId, "inventory"));
          batch.set(newDocRef, {
            ...item,
            createdAt: serverTimestamp(),
            createdBy: uid || null,
          });
        }

        batchCount++;
        if (batchCount >= BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          batchCount = 0;
        }
      }

      // Commit any remaining operations
      if (batchCount > 0) {
        await batch.commit();
      }

      setImportStatus("Import completed successfully!");
      setTimeout(() => {
        onInventoryUpdate();
        setShowImportConfirm(false);
        setPendingImportRows([]);
        setImportPreview(null);
        setImportPreviewError("");
        setImporting(false);
        setImportProgress(0);
        setImportStatus("");
      }, 1000);

    } catch (err) {
      console.error("[InventoryImport] Import error:", err);
      setImportPreviewError(`Import failed: ${err.message}`);
      setImporting(false);
      setImportProgress(0);
      setImportStatus("");
    }
  };

  const downloadTemplate = () => {
    const baseRows = getTemplateRows(templateId);

    const customExample = {};
    fieldDefinitions.forEach((def) => {
      const key = def.key || def.id;
      if (!key) return;

      const type = String(def.fieldType || def.type || "text").toLowerCase();

      let example = "";
      if (type === "number") example = "123";
      else if (type === "boolean") example = "Yes/No";
      else if (type === "select" && Array.isArray(def.options)) example = def.options[0] || "";
      else example = def.label || key;

      customExample[key] = example;
    });

    const templateData = baseRows.map((r) => formatInventorySheetRow({ ...r, ...customExample }));

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InventoryTemplate");

    const chosen = TEMPLATES.find((t) => t.id === templateId)?.label || "Inventory";
    XLSX.writeFile(wb, `InventoryTemplate_${chosen.replace(/\s+/g, "_")}.xlsx`);
  };

  const exportInventory = async () => {
    if (!companyId) return;

    setExporting(true);
    try {
      const invColRef = collection(db, "companies", companyId, "inventory");
      const snap = await getDocs(invColRef);

      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      let customKeys = [];
      if (fieldDefinitions.length > 0) {
        customKeys = fieldDefinitions
          .map((def) => def.key || def.id)
          .filter(Boolean)
          .map((k) => String(k).trim())
          .filter(Boolean);
      } else {
        const set = new Set();
        items.forEach((it) => {
          const c = it?.custom && typeof it.custom === "object" ? it.custom : {};
          Object.keys(c || {}).forEach((k) => set.add(k));
        });
        customKeys = Array.from(set).sort((a, b) => a.localeCompare(b));
      }

      const rows = items.map((it) => {
        const { category, subCategory } = splitCategoryIfNeeded(it?.category, it?.subCategory);
        const sampleMetrics = sampleOrderMetricsByProduct.get(it.id) || {
          sampleOrderCount: 0,
          convertedOrderCount: 0,
        };
        const conversionRate = sampleMetrics.sampleOrderCount
          ? `${((sampleMetrics.convertedOrderCount / sampleMetrics.sampleOrderCount) * 100).toFixed(1)}%`
          : "0.0%";

        const core = {
          sku: it?.sku ?? "",
          name: it?.name ?? "",
          description: it?.description ?? "", // ✅ ADDED: Description in export
          category: category ?? "",
          subCategory: subCategory ?? "",
          packSize: it?.packSize ?? "",
          price: it?.price ?? "",
          stock: it?.stock ?? 0,
          lowStockThreshold: it?.lowStockThreshold ?? 10,
          sampleable: it?.sampleable ? "Yes" : "No",
          featured: it?.featured ? "Yes" : "No",
          groupIds: Array.isArray(it?.groupIds) ? it.groupIds.join(", ") : "",
          sampleOrderCount: sampleMetrics.sampleOrderCount,
          convertedOrderCount: sampleMetrics.convertedOrderCount,
          conversionToOrderPercent: conversionRate,
        };

        const custom = it?.custom && typeof it.custom === "object" ? it.custom : {};
        customKeys.forEach((k) => {
          core[k] = custom?.[k] ?? "";
        });

        return formatInventorySheetRow(core);
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "InventoryExport");

      const ts = new Date();
      const stamp = `${ts.getFullYear()}-${String(ts.getMonth() + 1).padStart(2, "0")}-${String(
        ts.getDate()
      ).padStart(2, "0")}`;

      XLSX.writeFile(wb, `InventoryExport_${stamp}.xlsx`);
    } catch (err) {
      console.error("[InventoryExport] Error exporting inventory:", err);
    } finally {
      setExporting(false);
    }
  };

  const selectedPack = FIELD_PACKS[templateId] || FIELD_PACKS.custom_select;

  const extraPackFields = useMemo(() => {
    const selectedPackKeys = new Set(
      (selectedPack.fields || [])
        .map((field) => normalizeKey(field.key || field.label))
        .filter(Boolean)
    );
    const seen = new Set();
    const extras = [];

    Object.values(FIELD_PACKS).forEach((pack) => {
      (pack?.fields || []).forEach((field) => {
        const key = normalizeKey(field.key || field.label);
        if (!key || selectedPackKeys.has(key) || seen.has(key)) return;
        seen.add(key);
        extras.push({
          ...field,
          key,
          packTitle: pack?.title || "Field Pack",
        });
      });
    });

    extras.sort((a, b) => (a.label || a.key).localeCompare(b.label || b.key));
    return extras;
  }, [FIELD_PACKS, selectedPack]);

  const selectedExtraFields = useMemo(
    () => extraPackFields.filter((field) => selectedExtraFieldKeys.includes(field.key)),
    [extraPackFields, selectedExtraFieldKeys]
  );

  const fieldsToApply = useMemo(
    () => [...(selectedPack.fields || []), ...selectedExtraFields],
    [selectedExtraFields, selectedPack]
  );

  const previewPack = useMemo(() => {
    const packFields = fieldsToApply;
    const existingKeys = new Set(
      (fieldDefinitions || [])
        .map((d) => normalizeKey(d.key || d.id || ""))
        .filter(Boolean)
    );

    const rows = packFields.map((f) => {
      const key = normalizeKey(f.key || f.label);
      const alreadyExists = existingKeys.has(key);
      return {
        ...f,
        key,
        alreadyExists,
      };
    });

    const toAdd = rows.filter((r) => !r.alreadyExists).length;
    const skipped = rows.filter((r) => r.alreadyExists).length;

    return { rows, toAdd, skipped };
  }, [fieldDefinitions, fieldsToApply]);

  useEffect(() => {
    if (!showPackModal) return;
    setSelectedExtraFieldKeys([]);
  }, [showPackModal, templateId]);

  const applySelectedPack = async () => {
    if (!companyId) return;

    setApplyingPack(true);
    setPackError("");

    try {
      const colRef = collection(db, "companies", companyId, "inventoryFieldDefinitions");
      const snap = await getDocs(colRef);
      const existing = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const existingKeySet = new Set(
        existing
          .map((d) => normalizeKey(d.key || d.id || ""))
          .filter(Boolean)
      );

      let maxOrder = 0;
      existing.forEach((d) => {
        if (typeof d.order === "number" && d.order > maxOrder) maxOrder = d.order;
      });

      const fields = fieldsToApply;

      let addedCount = 0;

      for (const f of fields) {
        const key = normalizeKey(f.key || f.label);
        if (!key) continue;

        if (existingKeySet.has(key)) continue;

        maxOrder += 1;

        const data = {
          key,
          label: String(f.label || key).trim(),
          type: f.type || "text",
          options: Array.isArray(f.options) ? f.options : [],
          showInTable: Boolean(f.showInTable),
          showInEdit: f.showInEdit !== false,
          showInCatalog: Boolean(f.showInCatalog),
          order: maxOrder,
          createdAt: serverTimestamp(),
          source: `fieldPack:${templateId}`,
        };

        await addDoc(colRef, data);
        existingKeySet.add(key);
        addedCount += 1;
      }

      await loadDefs(companyId);
      setShowPackModal(false);

      if (addedCount === 0) console.log("[FieldPack] No new fields added (all existed).");
      else console.log(`[FieldPack] Added ${addedCount} fields for ${templateId}.`);
    } catch (err) {
      console.error("[FieldPack] Error applying pack:", err);
      setPackError("Failed to apply this pack. Please try again.");
    } finally {
      setApplyingPack(false);
    }
  };

  const pill = (text, className) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] ${className}`}>
      {text}
    </span>
  );

  // --- Match AccountActions UI classes ---
  const outlineBtn =
    "inline-flex items-center gap-2 h-10 px-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-[13px] font-medium whitespace-nowrap";
  const outlineBtnDisabled = "disabled:opacity-50 disabled:cursor-not-allowed";
  const selectBase =
    "h-9 px-3 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-[13px] text-slate-700";

  // ---------------------------
  // Dropdown positioning + closing (PORTAL)
  // ---------------------------
  const MENU_WIDTH = 224; // w-56
  const MENU_GAP = 8;

  const computeMenuPos = useCallback(() => {
    const btn = actionsBtnRef.current;
    if (!btn) return;

    const r = btn.getBoundingClientRect();
    const viewportW = window.innerWidth || 0;

    // Prefer right-aligned with button
    let left = r.right - MENU_WIDTH;
    if (left < 8) left = 8;
    if (left + MENU_WIDTH > viewportW - 8) left = Math.max(8, viewportW - MENU_WIDTH - 8);

    const top = r.bottom + MENU_GAP;

    setMenuPos({ top, left, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (!showActionsMenu) return;
    computeMenuPos();
  }, [showActionsMenu, computeMenuPos]);

  useEffect(() => {
    if (!showActionsMenu) return;

    const onResize = () => computeMenuPos();
    const onScroll = () => computeMenuPos();

    window.addEventListener("resize", onResize);
    // capture scroll from any container
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [showActionsMenu, computeMenuPos]);

  useEffect(() => {
    if (!showActionsMenu) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    // Use pointerdown so it works on touch + mouse
    const onPointerDown = (e) => {
      const btn = actionsBtnRef.current;
      const menu = actionsMenuRef.current;

      if (btn && btn.contains(e.target)) return;
      if (menu && menu.contains(e.target)) return;

      setShowActionsMenu(false);
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [showActionsMenu]);

  const actionsDisabled = !companyId || importing || exporting;

  return (
    <div className="inline-flex items-center gap-2 relative">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Actions dropdown */}
      <div className="relative">
        <button
          ref={actionsBtnRef}
          type="button"
          onClick={() => {
            if (actionsDisabled) return;
            setShowActionsMenu((v) => !v);
          }}
          className={`${outlineBtn} ${outlineBtnDisabled}`}
          disabled={actionsDisabled}
          title="Inventory actions"
        >
          <span className="bg-gradient-to-r from-sky-700 via-blue-600 to-cyan-500 bg-clip-text text-transparent">
            Actions
          </span>
          <ChevronDown className="w-4 h-4 text-sky-500" />
        </button>

        {/* PORTAL MENU (cannot be clipped) */}
        {showActionsMenu ? (
          <Portal>
            <div
              ref={actionsMenuRef}
              className="fixed z-[9999] w-56 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden"
              style={{ top: `${menuPos.top}px`, left: `${menuPos.left}px` }}
            >
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                onClick={() => {
                  setShowActionsMenu(false);
                  setPackError("");
                  setShowPackModal(true);
                }}
                disabled={!companyId || applyingPack}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-slate-500" />
                  {applyingPack ? "Applying fields template…" : "Apply Fields Template"}
                </div>
              </button>

              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                onClick={() => {
                  setShowActionsMenu(false);
                  handleImportClick();
                }}
                disabled={actionsDisabled}
              >
                <div className="flex items-center gap-2">
                  <Upload className="w-4 h-4 text-slate-500" />
                  Import Excel
                </div>
              </button>

              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                onClick={() => {
                  setShowActionsMenu(false);
                  downloadTemplate();
                }}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Download Template
                </div>
              </button>

              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                onClick={async () => {
                  setShowActionsMenu(false);
                  await exportInventory();
                }}
                disabled={exporting}
                title="Export Firestore inventory to Excel"
              >
                <div className="flex items-center gap-2">
                  <Download className="w-4 h-4 text-slate-500" />
                  {exporting ? "Exporting…" : "Export Inventory"}
                </div>
              </button>
            </div>
          </Portal>
        ) : null}
      </div>

      {/* ✅ ADDED: Column mapper modal for description mapping */}
      {showColumnMapper && importPreview && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-semibold text-slate-900">Map Excel Columns</h3>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                We detected some columns that need mapping. Please match them to the correct fields.
              </p>
            </div>

            <div className="p-4 max-h-96 overflow-y-auto">
              <div className="space-y-3">
                {Object.keys(importPreview.columnMapping).map((excelCol) => (
                  <div key={excelCol} className="grid grid-cols-2 gap-3 items-center">
                    <div className="text-sm font-medium text-slate-700">{excelCol}</div>
                    <select
                      className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
                      value={importPreview.columnMapping[excelCol] || ""}
                      onChange={(e) => {
                        const newMapping = { ...importPreview.columnMapping };
                        newMapping[excelCol] = e.target.value;
                        setImportPreview({
                          ...importPreview,
                          columnMapping: newMapping,
                        });
                      }}
                    >
                      <option value="">-- Select Field --</option>
                      <option value="sku">SKU</option>
                      <option value="name">Product Name</option>
                      <option value="description">Description</option>
                      <option value="category">Category</option>
                      <option value="subCategory">Sub Category</option>
                      <option value="packSize">Pack Size</option>
                      <option value="price">Price</option>
                      <option value="stock">Stock</option>
                      <option value="lowStockThreshold">Low Stock</option>
                      <option value="sampleable">Sample-able</option>
                      <option value="featured">Featured</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowColumnMapper(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowColumnMapper(false);
                  // Update the column mapping and proceed
                  setColumnMapping(importPreview.columnMapping);
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700"
              >
                Save Mapping
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ADDED: Import progress modal */}
      {importing && importProgress > 0 && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <h3 className="text-sm font-semibold text-slate-900">Importing Inventory</h3>
              </div>
              <p className="text-xs text-slate-500 mt-1">{importStatus}</p>
            </div>
            
            <div className="p-4">
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress}%` }}
                ></div>
              </div>
              <div className="text-xs text-slate-600 mt-2 text-center">
                {importProgress}% complete ({Math.round((importProgress / 100) * pendingImportRows.length)}/{pendingImportRows.length} items)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Confirm Modal */}
      {showImportConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Confirm Inventory Import</div>
                <div className="text-xs text-slate-500 mt-1">
                  Existing items are matched and updated by <strong>current SKU</strong> first, then by <strong>Previous SKU</strong>.
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingImportRows([]);
                  setImportPreview(null);
                  setImportPreviewError("");
                }}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              {importPreviewError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-xs">
                  {importPreviewError}
                </div>
              )}

              {importPreview ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Rows in file</div>
                      <div className="text-lg font-semibold text-slate-900">{importPreview.rowsTotal}</div>
                    </div>
                    <div className="border border-slate-200 rounded-xl p-3">
                      <div className="text-xs text-slate-500">Columns detected</div>
                      <div className="text-lg font-semibold text-slate-900">{importPreview.columnsDetected}</div>
                    </div>

                    <div className="border border-emerald-200 bg-emerald-50 rounded-xl p-3">
                      <div className="text-xs text-emerald-700">Will UPDATE (SKU match)</div>
                      <div className="text-lg font-semibold text-emerald-900">{importPreview.willUpdate}</div>
                    </div>

                    <div className="border border-blue-200 bg-blue-50 rounded-xl p-3">
                      <div className="text-xs text-blue-700">Will CREATE (new SKU)</div>
                      <div className="text-lg font-semibold text-blue-900">{importPreview.willCreate}</div>
                    </div>
                  </div>

                  {/* ✅ ADDED: Column mapping preview */}
                  {Object.keys(importPreview.columnMapping).length > 0 && (
                    <div className="border border-blue-200 bg-blue-50 rounded-xl p-3 text-xs text-blue-900">
                      <div className="font-semibold flex items-center gap-2">Column Mapping Detected</div>
                      <div className="mt-2 space-y-1">
                        {Object.entries(importPreview.columnMapping).map(([excelCol, ourField]) => (
                          <div key={excelCol} className="flex items-center gap-2">
                            <span className="font-medium">{excelCol}</span>
                            <span className="text-blue-700">→</span>
                            <span className="bg-white px-2 py-0.5 rounded border border-blue-200">
                              {ourField}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-xs text-amber-900">
                    <div className="font-semibold flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Overwrite warning
                    </div>
                    <div className="mt-1">
                      This import includes <strong>{importPreview.overwriteFieldCount}</strong> column(s) that can overwrite
                      existing inventory fields.
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      {(importPreview.overwriteFields || []).map((f) =>
                        pill(f, "bg-white/70 border border-amber-200 text-amber-900")
                      )}
                      {(importPreview.overwriteCustomFields || []).slice(0, 8).map((f) =>
                        pill(`custom: ${f}`, "bg-white/70 border border-amber-200 text-amber-900")
                      )}
                      {(importPreview.overwriteCustomFields || []).length > 8
                        ? pill(
                            `+${importPreview.overwriteCustomFields.length - 8} more custom…`,
                            "bg-white/70 border border-amber-200 text-amber-900"
                          )
                        : null}
                    </div>
                  </div>

                  {(importPreview.skipMissingSku > 0 ||
                    importPreview.skipDuplicateSkuInDb > 0 ||
                    importPreview.skipDuplicateSkuInFile > 0 ||
                    importPreview.duplicateSkusInDbCount > 0) && (
                    <div className="border border-slate-200 bg-slate-50 rounded-xl p-3 text-xs text-slate-700 space-y-1">
                      <div className="font-semibold text-slate-900">Safety skips</div>
                      {importPreview.skipMissingSku > 0 && (
                        <div>• {importPreview.skipMissingSku} row(s) skipped (missing SKU)</div>
                      )}
                      {importPreview.skipDuplicateSkuInFile > 0 && (
                        <div>• {importPreview.skipDuplicateSkuInFile} row(s) skipped (duplicate SKU in file)</div>
                      )}
                      {importPreview.skipDuplicateSkuInDb > 0 && (
                        <div>• {importPreview.skipDuplicateSkuInDb} row(s) skipped (SKU is duplicated in Firestore)</div>
                      )}
                      {importPreview.duplicateSkusInDbCount > 0 && (
                        <div className="text-slate-500">Fix duplicate SKUs in your inventory to allow safe updates by SKU.</div>
                      )}
                    </div>
                  )}

                  <div className="text-[11px] text-slate-500">
                    Note: New items always default to <strong>groupIds: {JSON.stringify(DEFAULT_GROUP_IDS)}</strong>. Existing
                    items keep their groupIds unless you explicitly include a groupIds column.
                  </div>
                </>
              ) : (
                <div className="text-xs text-slate-600">Building import preview…</div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={() => {
                  setShowImportConfirm(false);
                  setPendingImportRows([]);
                  setImportPreview(null);
                  setImportPreviewError("");
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!importPreview || importing}
                onClick={async () => {
                  try {
                    setImporting(true);
                    await importInventoryData(pendingImportRows, importPreview.columnMapping);
                    onInventoryUpdate?.();
                    setShowImportConfirm(false);
                    setPendingImportRows([]);
                    setImportPreview(null);
                    setImportPreviewError("");
                  } catch (err) {
                    console.error("[InventoryImport] Import error:", err);
                    setImportPreviewError(`Import failed: ${err.message}`);
                  } finally {
                    setImporting(false);
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                title="Proceed with import"
              >
                {importing ? "Importing…" : "Yes, Import & Overwrite"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pack Preview / Confirm Modal */}
      {showPackModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-5xl rounded-2xl bg-white shadow-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">{selectedPack.title}</div>
                <div className="text-xs text-slate-500 mt-1">{selectedPack.description}</div>
              </div>

              <button
                type="button"
                onClick={() => setShowPackModal(false)}
                className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-md hover:bg-slate-50"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-4">
              {packError && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-lg text-xs">
                  {packError}
                </div>
              )}

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  Apply Fields Template
                </label>
                <select
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                  className={`${selectBase} w-full`}
                  title="Choose a fields template"
                >
                  {TEMPLATES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {fieldsToApply.length === 0 && extraPackFields.length === 0 ? (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  This pack doesn't add any fields. Use your{" "}
                  <strong>Inventory Field Definitions</strong> screen to add custom columns.
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.85fr)]">
                  <div className="space-y-3 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-xs text-slate-600">
                        You're about to add{" "}
                        <span className="font-semibold text-slate-900">{previewPack.toAdd}</span> new fields.
                        {previewPack.skipped > 0 && (
                          <>
                            {" "}
                            <span className="text-slate-500">
                              ({previewPack.skipped} already exist and will be skipped)
                            </span>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {pill("Core fields stay standard", "bg-slate-100 text-slate-700")}
                        {pill("Stored in item.custom", "bg-emerald-50 text-emerald-700")}
                      </div>
                    </div>

                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 text-[11px] uppercase tracking-wide text-slate-500 flex justify-between">
                        <span>Field</span>
                        <span>Status</span>
                      </div>

                      <div className="max-h-80 overflow-auto divide-y divide-slate-200">
                        {previewPack.rows.length === 0 ? (
                          <div className="px-3 py-4 text-xs text-slate-500">
                            Pick fields on the right to build a custom template.
                          </div>
                        ) : (
                          previewPack.rows.map((f) => (
                            <div key={f.key} className="px-3 py-2 flex items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-medium text-slate-900">{f.label}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-1.5 items-center">
                                  {pill(`key: ${f.key}`, "bg-slate-100 text-slate-700")}
                                  {pill(`type: ${f.type}`, "bg-blue-50 text-blue-700")}
                                  {f.type === "select" && Array.isArray(f.options) && f.options.length > 0
                                    ? pill(
                                        `options: ${f.options.slice(0, 3).join(", ")}${f.options.length > 3 ? "…" : ""}`,
                                        "bg-purple-50 text-purple-700"
                                      )
                                    : null}
                                </div>

                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {f.showInTable && pill("Table", "bg-blue-50 text-blue-700")}
                                  {f.showInEdit && pill("Edit", "bg-emerald-50 text-emerald-700")}
                                  {f.showInCatalog && pill("Catalog", "bg-purple-50 text-purple-700")}
                                  {f.packTitle ? pill(f.packTitle, "bg-amber-50 text-amber-700") : null}
                                </div>
                              </div>

                              <div className="pt-0.5">
                                {f.alreadyExists
                                  ? pill("Already exists", "bg-slate-100 text-slate-600")
                                  : pill("Will be added", "bg-green-50 text-green-700")}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-sm font-semibold text-slate-900">Add extra custom fields</div>
                      <div className="mt-1 text-xs text-slate-500">
                        Select any extra fields you want to add alongside this template.
                      </div>
                    </div>

                    <div className="max-h-80 overflow-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
                      {extraPackFields.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-slate-500">
                          No additional template fields are available.
                        </div>
                      ) : (
                        extraPackFields.map((field) => {
                          const checked = selectedExtraFieldKeys.includes(field.key);
                          return (
                            <label
                              key={field.key}
                              className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                checked={checked}
                                onChange={(e) =>
                                  setSelectedExtraFieldKeys((prev) =>
                                    e.target.checked
                                      ? [...prev, field.key]
                                      : prev.filter((key) => key !== field.key)
                                  )
                                }
                              />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-900 leading-5">{field.label}</div>
                                <div className="mt-0.5 flex flex-wrap gap-1.5 text-[11px] text-slate-500">
                                  {pill(field.packTitle, "bg-amber-50 text-amber-700")}
                                  {pill(`key: ${field.key}`, "bg-slate-100 text-slate-700")}
                                  {pill(`type: ${field.type}`, "bg-blue-50 text-blue-700")}
                                </div>
                              </div>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="lg:col-span-2 text-[11px] text-slate-500">
                    Note: Applying a pack does <strong>not</strong> change any existing inventory item data — it only
                    adds new custom field definitions so your UI + imports can use those columns.
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-white">
              <button
                type="button"
                onClick={() => setShowPackModal(false)}
                className="px-3 py-1.5 text-sm font-medium rounded-xl border border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={applySelectedPack}
                disabled={applyingPack || fieldsToApply.length === 0 || previewPack.toAdd === 0}
                className="px-3 py-1.5 text-sm font-medium rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60"
                title={previewPack.toAdd === 0 ? "Nothing new to add" : "Add these fields"}
              >
                {applyingPack
                  ? "Applying…"
                  : previewPack.toAdd === 0
                  ? "No new fields"
                  : `Add ${previewPack.toAdd} fields`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryActions;
